// src/pages/Platform/PlatformGroups.js
//
// The "sales vs support get different permission levels" screen itself —
// every PlatformGroup and its own Permissions (handlers.
// ListPlatformGroupsHandler/CreatePlatformGroupHandler/
// UpdatePlatformGroupHandler, task #202), add/edit inline via
// GroupModal below, same pattern Companies.js's own AddCompanyModal uses.
import { useState, useEffect, useCallback, useContext } from "react";
import {
  getPlatformGroups,
  getPlatformPermissions,
  createPlatformGroup,
  updatePlatformGroup,
  deletePlatformGroup,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

function GroupModal({ token, existingGroup, permissions, onClose, onSaved }) {
  const isEdit = !!existingGroup;
  const [name, setName] = useState(existingGroup?.name || "");
  const [description, setDescription] = useState(existingGroup?.description || "");
  const [permissionIds, setPermissionIds] = useState(existingGroup?.permissions?.map((p) => p.id) || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const togglePermission = (id) => {
    setPermissionIds((ids) => (ids.includes(id) ? ids.filter((p) => p !== id) : [...ids, id]));
  };

  // Group permissions by module (company/platform/...) so the checkbox list
  // reads as sections instead of one long flat list — same grouping
  // ListPlatformPermissionsHandler's own "module, name" ordering implies.
  const byModule = permissions.reduce((acc, p) => {
    (acc[p.module] = acc[p.module] || []).push(p);
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { name, description, permission_ids: permissionIds };
      if (isEdit) {
        await updatePlatformGroup(token, existingGroup.id, payload);
      } else {
        await createPlatformGroup(token, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Failed to save group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <form onSubmit={handleSubmit}>
          <h3 style={{ marginTop: 0 }}>{isEdit ? "Edit group" : "Add group"}</h3>

          <label style={labelStyle}>Name</label>
          <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} required />

          <label style={labelStyle}>Description</label>
          <input style={inputStyle} value={description} onChange={(e) => setDescription(e.target.value)} />

          <label style={labelStyle}>Permissions</label>
          {Object.keys(byModule).map((mod) => (
            <div key={mod} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 }}>
                {mod}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {byModule[mod].map((p) => (
                  <label key={p.id} style={chipStyle(permissionIds.includes(p.id))}>
                    <input
                      type="checkbox"
                      checked={permissionIds.includes(p.id)}
                      onChange={() => togglePermission(p.id)}
                      style={{ marginRight: 6 }}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {error && <div style={errorBoxStyle}>{error}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button type="button" style={secondaryBtnStyle} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" style={primaryBtnStyle} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PlatformGroups() {
  const { token } = useContext(PlatformAuthContext);
  const [groups, setGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, p] = await Promise.all([getPlatformGroups(token), getPlatformPermissions(token)]);
      setGroups(g || []);
      setPermissions(p || []);
    } catch (err) {
      console.error("Failed to load platform groups:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete the "${g.name}" group? Members lose its permissions immediately.`)) return;
    try {
      await deletePlatformGroup(token, g.id);
      load();
    } catch (err) {
      alert(err.message || "Failed to delete group");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Platform groups</h2>
        <button
          style={primaryBtnStyle}
          onClick={() => {
            setEditingGroup(null);
            setShowModal(true);
          }}
        >
          + Add group
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {loading ? (
          <div style={{ color: "#94a3b8" }}>Loading...</div>
        ) : groups.length === 0 ? (
          <div style={{ color: "#94a3b8" }}>No groups yet.</div>
        ) : (
          groups.map((g) => (
            <div key={g.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>{g.name}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{g.description}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
                {(g.permissions || []).length} permission(s)
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button
                  style={secondaryBtnStyle}
                  onClick={() => {
                    setEditingGroup(g);
                    setShowModal(true);
                  }}
                >
                  Edit
                </button>
                <button style={{ ...secondaryBtnStyle, color: "#dc2626" }} onClick={() => handleDelete(g)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <GroupModal
          token={token}
          existingGroup={editingGroup}
          permissions={permissions}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
    </div>
  );
}

const chipStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  fontSize: 12,
  padding: "5px 9px",
  borderRadius: 999,
  border: `1px solid ${active ? "#0f172a" : "#cbd5e1"}`,
  background: active ? "#0f172a" : "#fff",
  color: active ? "#fff" : "#334155",
  cursor: "pointer",
});
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer" };
const errorBoxStyle = { background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 12 };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: 480, maxWidth: "90vw", maxHeight: "85vh", overflowY: "auto" };
